/**
 * MEIA Studio — Validation + Context-Builder Runtime Constants
 *
 * Version strings, schema acceptance lists, classification rank map,
 * and the validation rule registry — extracted from the MEIA reference
 * `validation-types.ts` and `context-builder-types.ts` so the type
 * files stay declaration-only.
 *
 * Kill-web rules (BV-042..045) are intentionally omitted: kill-web is
 * out of scope for the Azymmetric MEIA Studio v1 port.
 */
import { ClassificationLevel } from '../types/meia/context-builder.types';
// ─────────────────────────────────────────────────────────────
// StudyMissionContext Field Limits
// ─────────────────────────────────────────────────────────────
// Single source of truth for mission-context max lengths. Imported by both
// the backend DTO (`study-mission.dto.ts`) and the frontend Zod schema
// (`apps/web/src/schemas/meia.schema.ts`) so the two cannot drift.
export const STUDY_MISSION_CONTEXT_MAX_LENGTHS = {
    areaOfOperations: 2000,
    assumptionsConstraints: 4000,
    authoritiesRoeEnvelope: 2000,
    blueForceComposition: 2000,
    commandersIntent: 2000,
    desiredMissionOutcome: 2000,
    timeWindow: 1000,
};
// ─────────────────────────────────────────────────────────────
// Version Constants
// ─────────────────────────────────────────────────────────────
export const VALIDATION_VERSION = '1.1.0';
/**
 * Azymmetric baseline: v4.3.5. The MEIA reference read `'4.3.4'` in
 * `validation-types.ts:45` — we ship v4.3.5 as the canonical schema
 * version for this port.
 */
export const CURRENT_SCHEMA_VERSION = '4.3.5';
// v4.3.4 release note from the reference: algorithm version deliberately
// UNCHANGED at 1.2.0. killWeb_data.edges[] was declarative provenance in
// v1.2.0 — not read by contest math. Bumping to 1.3.0 would mark every
// existing v4.3.3-scored study STALE on re-open (see staleness.ts BV-002).
// In this Azymmetric port kill-web is dropped entirely; algorithm
// version remains 1.2.0 for v4.3.5 as well.
export const CURRENT_ALGORITHM_VERSION = '1.2.0';
/**
 * v4.3.4 bumped the context-builder version from 1.3.0 to record the
 * (now-dropped) kill-web pass-through. We retain 1.4.0 as the port's
 * baseline to keep numbering contiguous with upstream.
 *
 * v4.3.3 bump to 1.3.0 recorded the options-inbox /
 * effectTypes-override resolver (`resolveEffectiveEffectTypes`) and
 * the raw-type extensions for task.technologyOptions[],
 * task.technologies[].effectTypesOverride[], and
 * technologyLibrary_data[].{effectTypes,evaluationProvenance}.
 *
 * Prior bumps: 1.1.0 → 1.2.0 for v4.3 component-granularity MOP
 * shadowing, classification propagation, engagement-status lifecycle.
 * Kept in sync with CURRENT_ALGORITHM_VERSION so the pre-flight
 * `validateAlgorithmVersion()` check does not emit a WARNING for
 * freshly-built contexts.
 */
export const CONTEXT_BUILDER_VERSION = '1.4.0';
// ─────────────────────────────────────────────────────────────
// Accepted Schema Versions
// ─────────────────────────────────────────────────────────────
/**
 * Accepted schema versions — parser reads v4.1, v4.2, v4.3, v4.3.3,
 * v4.3.4, and v4.3.5 studies (§6 of the v4.3 spec + v4.3.3 / v4.3.4 /
 * v4.3.5 additive additions). BV-001 uses this list to decide whether
 * a study is parseable as-is or requires migration.
 */
export const ACCEPTED_SCHEMA_VERSIONS = [
    '4.1.0',
    '4.2.0',
    '4.3.0',
    '4.3.3',
    '4.3.4',
    '4.3.5',
];
// ─────────────────────────────────────────────────────────────
// Classification Rank
// ─────────────────────────────────────────────────────────────
/**
 * Ordered rank for classification comparison. A component's level
 * must be >= its parent system's level (upward-only override).
 */
export const CLASSIFICATION_RANK = {
    [ClassificationLevel.UNCLASSIFIED]: 0,
    [ClassificationLevel.CUI]: 1,
    [ClassificationLevel.SECRET]: 2,
    [ClassificationLevel.TOP_SECRET]: 3,
};
// ─────────────────────────────────────────────────────────────
// Validation Rules Registry
// ─────────────────────────────────────────────────────────────
/**
 * All validation rules defined by schema blocking_validations.
 * Order: blocking validations first, then structural/integrity checks.
 */
export const VALIDATION_RULES = [
    {
        id: 'BV-001',
        category: 'SCHEMA_VERSION',
        description: 'Schema version must match current schema version (4.1.0) or application must migrate',
        megReference: null,
        blocking: true,
    },
    {
        id: 'BV-002',
        category: 'ALGORITHM_VERSION',
        description: 'Algorithm version mismatch indicates stale scoring outputs',
        megReference: null,
        blocking: false, // WARNING only
    },
    {
        id: 'BV-003',
        category: 'MOE_THRESHOLD',
        description: 'MOE threshold and objective must be non-empty strings before a run case is executed against that MOE',
        megReference: null,
        blocking: true,
    },
    {
        id: 'BV-004',
        category: 'NON_MATERIEL',
        description: 'At least one architectureAlternatives entry must have dotmlpfpCategory != "Materiel" before resultsAndRecommendations.approvedForRelease can be set to true',
        megReference: 'MEG v2 Sec 3.2d',
        blocking: false, // WARNING normally, ERROR if checkPublicationReadiness=true
    },
    {
        id: 'BV-005',
        category: 'MODEL_REGISTRY',
        description: 'Any run case with COMPUTED MOPValues must reference at least one modelRegistry entry for MEG compliance',
        megReference: 'MEG v2 Sec 3.2',
        blocking: false, // WARNING only
    },
    {
        id: 'BV-006',
        category: 'OVERRIDE_AUDIT',
        description: 'All override objects with applied=true must have non-null: overrideBand, rationale, authorizedBy, timestamp (HITL audit compliance)',
        megReference: 'DoD AI Ethics Policy',
        blocking: true,
    },
    {
        id: 'SI-001',
        category: 'WEIGHT_SUM',
        description: 'For each MOE, contributing task weights must sum to 1.0 (±0.001 tolerance)',
        megReference: null,
        blocking: true,
    },
    {
        id: 'SI-002',
        category: 'MOP_REFERENCE',
        description: 'Every task.requiredMOP[] ID must exist in mission.measuresOfPerformance[].id',
        megReference: null,
        blocking: false, // WARNING for orphaned refs
    },
    {
        id: 'SI-003',
        category: 'TASK_LINKAGE',
        description: 'Bidirectional task↔MOE linkage: task.linkedMOE must reference valid MOE, and MOE.linkedTasks must include task',
        megReference: null,
        blocking: false, // WARNING for broken links
    },
    {
        id: 'SI-004',
        category: 'PARAMETER_RANGE',
        description: 'All scenario parameters and overrides must be in [0.0, 1.0] range',
        megReference: null,
        blocking: true,
    },
    {
        id: 'SI-005',
        category: 'SCORING_CONFIG',
        description: 'MOE and MOS scoring configurations must have all required fields populated',
        megReference: null,
        blocking: true,
    },
    {
        id: 'SI-006',
        category: 'STRUCTURAL_INTEGRITY',
        description: 'Structural cross-references and data integrity checks (mission links, etc)',
        megReference: null,
        blocking: true,
    },
    // ── v4.2 engagement-model pre-flight rules (§5.5) ──────────────
    {
        id: 'BV-007',
        category: 'ENGAGEMENT_DIVERGENCE',
        description: 'Target system carries both targetMOPs[] and engagedByTechnologies[].pkEstimate. Run-time divergence will be checked against contestConfig.mismatchWarningTolerance (default 0.15) by the engagement model.',
        megReference: null,
        blocking: false, // WARNING only — coexistence advisory at pre-flight
    },
    {
        id: 'BV-008',
        category: 'ALTERNATE_PATH_SCOPE',
        description: 'alternatePaths[].fallbackTaskId must reference a task in the same mission thread; crossSwimlane=false additionally requires same swimlane.',
        megReference: null,
        blocking: true,
    },
    {
        id: 'BV-010',
        category: 'ENGAGEMENT_BINDING',
        description: 'For any targetSystems_data.engagedByTechnologies[] binding, either targetMOPs[] on the target system must be non-empty OR pkEstimate must be authored on the binding; both absent is unscorable.',
        megReference: null,
        blocking: true,
    },
    {
        id: 'BV-012',
        category: 'RED_DETECTION_MISSING',
        description: 'Swimlane contains a task with phaseTimingConstraint (Timing-contest candidate) but no task in the swimlane flags redDetectionTrigger=true. Engagement model will fall back to whole-phase latency sum.',
        megReference: null,
        blocking: false, // WARNING only
    },
    {
        id: 'BV-013',
        category: 'RED_DETECTION_AMBIGUOUS',
        description: "More than one task in the same swimlane flags redDetectionTrigger=true. The start of Red's clock is ambiguous.",
        megReference: null,
        blocking: true,
    },
    {
        id: 'BV-018',
        category: 'GPS_TRIGGER_INERT',
        description: 'alternatePaths[] carries a GPSDenied trigger but no Blue technology in the study declares a GPS-dependent sensor or a communicationsLink whose bearerTechnologyId references a GPS-dependent technology. The trigger may be operationally inert.',
        megReference: null,
        blocking: false, // WARNING only
    },
    // ── v4.3 component-granularity pre-flight rules (§5) ───────────
    {
        id: 'BV-019',
        category: 'COMPONENT_REFERENCE',
        description: 'engagedByTechnologies[].targetComponentId, if non-null, must reference a component declared in the referenced targetSystem.targetComponents[]. A component cannot float free of its system — targetSystemId must also be populated.',
        megReference: null,
        blocking: true,
    },
    {
        id: 'BV-020',
        category: 'COMPONENT_DEPENDENCY_GRAPH',
        description: 'targetComponents[].dependencies must reference valid component IDs within the same target system. No forward references, no cross-system references, no self-references, no cycles on the dependency graph.',
        megReference: null,
        blocking: true,
    },
    {
        id: 'BV-021',
        category: 'COMPONENT_MOP_FALLBACK',
        description: 'Component-scoped engagement (targetComponentId non-null) is using system-level targetMOPs because the bound component declares no componentMOPs. Non-blocking but flagged in explainability — analyst can decide whether to author component-scoped MOPs or confirm system-level semantics were intended (v4.3 spec §3.2a).',
        megReference: null,
        blocking: false, // WARNING only
    },
    {
        id: 'BV-022',
        category: 'DEAD_COMPONENT_REGISTRY',
        description: 'A targetSystem declares a non-empty targetComponents[] registry but no engagedByTechnologies[] binding in the study references any of those components (all bindings are system-scoped). Flags dead decomposition — either drop the registry or add component-scoped engagements.',
        megReference: null,
        blocking: false, // WARNING only
    },
    {
        id: 'BV-023',
        category: 'COMPONENTS_ABSENT',
        description: 'v4.2-compatibility mode: no targetSystem in the study declares a targetComponents[] registry. Study will score identically to v4.2. Informational — confirms rollout safety. Also notes v4.3.1a migration behavior: engagements without explicit status render as draft until promoted.',
        megReference: null,
        blocking: false, // INFO only
    },
    {
        id: 'BV-024',
        category: 'CLASSIFICATION_MONOTONICITY',
        description: 'targetComponents[].classification, when present, must be at the same level or higher than the parent targetSystems[].classification. Classification is monotonic — cannot declare an UNCLASSIFIED component within a SECRET system. Ordering: UNCLASSIFIED < CUI < SECRET < TOP SECRET.',
        megReference: null,
        blocking: true,
    },
    {
        id: 'BV-025',
        category: 'RETIRED_STATUS_SCORED',
        description: 'An engagement with status "retired" or "rejected" is being scored as part of an active study. Non-blocking — the analyst may have explicitly chosen to retain retired engagements — but flagged in explainability to catch "why is this scoring if I said it\'s retired?" confusion (v4.3.1a §3.6).',
        megReference: null,
        blocking: false, // WARNING only
    },
    // ── v4.3.3 options-inbox / effectTypes governance rules ────────
    // BV range 036-041 sits ABOVE v4.4's reserved 026-035 to avoid
    // namespace collision. BV-041 is admin-service scoped (lives in
    // the admin backend for Effect-backed enrichment), not in
    // meia-studio.
    {
        id: 'BV-036',
        category: 'TECH_REFERENCE_INTEGRITY',
        description: 'task.technologies[].id MUST reference an existing technologyLibrary_data[].id. Options accepted from task.technologyOptions[] project into task.technologies[] via the same id; no dangling refs permitted. Scoring blocked for any task with an unresolved technology reference.',
        megReference: null,
        blocking: true,
    },
    {
        id: 'BV-037',
        category: 'OPTIONS_INBOX_CONSISTENCY',
        description: 'task.technologyOptions[] entries MUST have decisionState in {PENDING | ACCEPTED | REJECTED}. ACCEPTED options MUST have a corresponding task.technologies[] entry with the same technologyId. REJECTED options MUST NOT appear in task.technologies[]. Inbox/canvas divergence blocks scoring.',
        megReference: null,
        blocking: true,
    },
    {
        id: 'BV-038',
        category: 'EFFECT_TYPE_OVERRIDE_SUBSET',
        description: 'task.technologies[].effectTypesOverride[], when present, MUST contain non-empty effect names resolved from the existing Effect entity by the API mapper. Invalid entries block scoring for the task.',
        megReference: null,
        blocking: true,
    },
    {
        id: 'BV-039',
        category: 'EFFECT_TYPES_EMPTY',
        description: 'technologyLibrary_data[] snapshot row has empty effectTypes[] AND is bound to a task with no effectTypesOverride[] for that technology. ME attention required: either add an override on the task or update the source Effect mapping. Non-blocking; surfaced in the task inspector.',
        megReference: null,
        blocking: false, // WARNING only
    },
    {
        id: 'BV-040',
        category: 'OUT_OF_UNIVERSE_SELECTION',
        description: 'architectureAlternatives[].techSelections[] references a technologyId that is NOT in the union of any task.technologies[] for the same mission thread, AND the alternative was built with expandToFullCatalog=false. Flags an out-of-Phase-3-universe selection; non-blocking but surfaced in the Alternative editor.',
        megReference: null,
        blocking: false, // WARNING only
    },
    // ── v4.3.5 ACR register (acrEntries_data) rules ───────────────
    // Declarative-only in v1.2.0 — the scoring kernel does not read
    // acrEntries. These three rules are the sole consumers of
    // acrEntries_data in v4.3.5. Round 2 Gap #19 locked 2026-04-19.
    {
        id: 'BV-046',
        category: 'ACR_RISK_FIELDS_INCOMPLETE',
        description: "acrEntries_data[] entries with category='Risk' MUST declare both probability and severity. Assumption and Constraint entries are exempt. Missing fields indicate an incomplete Risk entry that cannot be properly triaged at G4 sign-off; blocking.",
        megReference: null,
        blocking: true,
    },
    {
        id: 'BV-047',
        category: 'ACR_LINK_RESOLUTION',
        description: "acrEntries_data[] entries declare optional linkedThreads[], linkedTasks[], linkedTechnologies[]. Every referenced id MUST resolve to its respective collection (missionThreads_data[].id, any missionThreads_data[].swimlanes[].tasks[].id, or technologyLibrary_data[].id). Dangling refs fire WARNING during authoring, ERROR when gateSignOffAttempted === 'G4' (downstream analyst-facing scope for the entry becomes undefined).",
        megReference: null,
        blocking: false, // WARNING during authoring; escalates to ERROR at G4
    },
    {
        id: 'BV-048',
        category: 'ACR_GATE_SIGN_OFF_BLOCKER',
        description: "G4 sign-off is blocked when any acrEntries_data[] entry has category='Risk', status='open', and mitigationApproach is null or empty. Fires only when gateSignOffAttempted === 'G4'. Analysts must either document a mitigationApproach or move the Risk to status='mitigated' / 'accepted' / 'closed' before G4.",
        megReference: null,
        blocking: true,
    },
];
